import React, { useEffect, useState, useRef } from "react";

import {
    getAllTrainer,
    deleteTrainer,
    updateTrainerStatus
} from "../services/trainerService";

import {
    Trash2,
    Edit,
    Download,
    Users,
    Search,
    Filter,
    RotateCcw,
    Upload,
    FileSpreadsheet,
    Plus
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";

import "./admin.css";


function TrainerList() {

    const navigate = useNavigate();

    const fileInputRef = useRef(null);


    // ==========================================
    // STATE
    // ==========================================

    const [trainers, setTrainers] = useState([]);

    const [filteredTrainers, setFilteredTrainers] =
        useState([]);

    const [search, setSearch] = useState("");

    const [skill, setSkill] = useState("");

    const [location, setLocation] = useState("");

    const [rate, setRate] = useState("");

    const [status, setStatus] = useState("");

    const [updatingStatusId, setUpdatingStatusId] =
        useState(null);

    const [currentPage, setCurrentPage] =
        useState(1);


    const recordsPerPage = 10;


    // ==========================================
    // SKILLS
    // ==========================================

    const skills = [
        "Java",
        "Python",
        "C++",
        "C",
        "DSA",
        "Spring Boot",
        "Hibernate",
        "SQL",
        "MERN Stack",
        "React JS",
        "Node JS",
        "AWS",
        "Azure",
        "DevOps",
        "Data Science",
        "Machine Learning",
        "Generative AI",
        "Power BI",
        "Cyber Security",
        "RPA",
        "UiPath",
        "Soft Skills",
        "Technical Writing",
        "Communication Skills"
    ];


    // ==========================================
    // LOCATIONS
    // ==========================================

    const locations = [
        "Noida",
        "Lucknow",
        "Kanpur",
        "Pune",
        "Mumbai",
        "Bangalore",
        "Hyderabad",
        "Bhopal",
        "Delhi",
        "Gurgaon",
        "Meerut",
        "Mohali",
        "Vizag",
        "Kochi",
        "Mangalore",
        "Hassan",
        "Gulbarga",
        "Karnataka"
    ];


    // ==========================================
    // LOAD TRAINERS
    // ==========================================

    useEffect(() => {

        loadTrainer();

    }, []);


    const loadTrainer = async () => {

        try {

            const response =
                await getAllTrainer();

            console.log(
                "Trainer API Response:",
                response.data
            );

            setTrainers(
                response.data || []
            );

            setFilteredTrainers(
                response.data || []
            );

        } catch (error) {

            console.error(
                "Failed to load trainers:",
                error
            );

            alert(
                "Failed to load trainer data."
            );

        }

    };


    // ==========================================
    // FILTER
    // ==========================================

    useEffect(() => {

        let data = [...trainers];


        // SEARCH
        if (search) {

            const searchValue =
                search.toLowerCase();


            data = data.filter(item =>

                item.name
                    ?.toLowerCase()
                    .includes(searchValue)

                ||

                item.email
                    ?.toLowerCase()
                    .includes(searchValue)

                ||

                item.phone
                    ?.includes(search)

                ||

                item.secondaryPhone
                    ?.includes(search)

                ||

                item.city
                    ?.toLowerCase()
                    .includes(searchValue)

                ||

                item.state
                    ?.toLowerCase()
                    .includes(searchValue)

            );

        }


        // SKILL
        if (skill) {

            data = data.filter(item =>

                item.skills
                    ?.toLowerCase()
                    .includes(
                        skill.toLowerCase()
                    )

            );

        }


        // LOCATION
        if (location) {

            data = data.filter(item =>

                item.city
                    ?.toLowerCase()
                    .includes(
                        location.toLowerCase()
                    )

            );

        }


        // RATE
        if (rate) {

            data = data.filter(item =>

                Number(item.rate || 0)
                <= Number(rate)

            );

        }


        // STATUS
        if (status) {

            data = data.filter(item =>

                item.status
                    ?.toLowerCase()
                    === status.toLowerCase()

            );

        }


        setFilteredTrainers(data);

        setCurrentPage(1);

    }, [
        search,
        skill,
        location,
        rate,
        status,
        trainers
    ]);


    // ==========================================
    // STATUS CLASS
    // ==========================================

    const getStatusClass = (
        trainerStatus
    ) => {

        if (!trainerStatus) {

            return "pending";

        }


        return trainerStatus
            .toLowerCase()
            .replace(/\s+/g, "-");

    };


    // ==========================================
    // UPDATE STATUS
    // ==========================================

    const handleStatusChange =
        async (
            id,
            newStatus
        ) => {

            if (!newStatus) {

                return;

            }


            try {

                setUpdatingStatusId(id);


                console.log(
                    "Updating trainer status:",
                    id,
                    newStatus
                );


                // API CALL
                const response =
                    await updateTrainerStatus(
                        id,
                        newStatus
                    );


                console.log(
                    "Status update response:",
                    response.data
                );


                // Update UI immediately
                setTrainers(prev =>
                    prev.map(trainer =>

                        trainer.id === id
                            ? {
                                ...trainer,
                                status:
                                    newStatus
                            }
                            : trainer

                    )
                );


            } catch (error) {

                console.error(
                    "Status update failed:",
                    error
                );


                console.error(
                    "API Error Response:",
                    error.response?.data
                );


                alert(
                    error.response?.data?.message ||
                    "Failed to update trainer status."
                );


                // Reload data
                await loadTrainer();


            } finally {

                setUpdatingStatusId(null);

            }

        };


    // ==========================================
    // PAGINATION
    // ==========================================

    const indexOfLastRecord =
        currentPage *
        recordsPerPage;


    const indexOfFirstRecord =
        indexOfLastRecord -
        recordsPerPage;


    const currentTrainers =
        filteredTrainers.slice(
            indexOfFirstRecord,
            indexOfLastRecord
        );


    const totalPages =
        Math.ceil(
            filteredTrainers.length /
            recordsPerPage
        );


    const changePage = (page) => {

        if (
            page < 1 ||
            page > totalPages
        ) {

            return;

        }


        setCurrentPage(page);

    };


    // ==========================================
    // CLEAR FILTER
    // ==========================================

    const clearFilter = () => {

        setSearch("");

        setSkill("");

        setLocation("");

        setRate("");

        setStatus("");

    };


    // ==========================================
    // DELETE
    // ==========================================

    const handleDelete =
        async (id) => {

            const confirmDelete =
                window.confirm(
                    "Are you sure you want to delete this trainer?"
                );


            if (!confirmDelete) {

                return;

            }


            try {

                await deleteTrainer(id);


                setTrainers(prev =>
                    prev.filter(
                        item =>
                            item.id !== id
                    )
                );


                alert(
                    "Trainer deleted successfully."
                );


            } catch (error) {

                console.error(
                    "Delete trainer error:",
                    error
                );


                alert(
                    "Failed to delete trainer."
                );

            }

        };


    // ==========================================
    // EXPORT EXCEL
    // ==========================================

    const handleExportExcel = () => {

        if (!filteredTrainers.length) {

            alert(
                "No trainer data available to export."
            );

            return;

        }


        const exportData =
            filteredTrainers.map(
                trainer => ({

                    ID:
                        trainer.id || "",

                    Name:
                        trainer.name || "",

                    Age:
                        trainer.age || "",

                    Phone:
                        trainer.phone || "",

                    "Secondary Phone":
                        trainer.secondaryPhone || "",

                    Email:
                        trainer.email || "",

                    "Secondary Email":
                        trainer.secondaryEmail || "",

                    Experience:
                        trainer.experience || "",

                    City:
                        trainer.city || "",

                    State:
                        trainer.state || "",

                    Pincode:
                        trainer.pincode || "",

                    Skills:
                        trainer.skills || "",

                    Rate:
                        trainer.rate || "",

                    Source:
                        trainer.source || "",

                    Status:
                        trainer.status ||
                        "PENDING",

                    Remark:
                        trainer.remark || "",

                    Resume:
                        trainer.resumeName || ""

                })
            );


        const worksheet =
            XLSX.utils.json_to_sheet(
                exportData
            );


        const workbook =
            XLSX.utils.book_new();


        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Trainers"
        );


        XLSX.writeFile(
            workbook,
            "CertED_Trainers.xlsx"
        );

    };


    // ==========================================
    // IMPORT CLICK
    // ==========================================

    const handleImportClick = () => {

        fileInputRef.current?.click();

    };


    // ==========================================
    // IMPORT EXCEL
    // ==========================================

    const handleImportExcel =
        (event) => {

            const file =
                event.target.files?.[0];


            if (!file) {

                return;

            }


            const reader =
                new FileReader();


            reader.onload = (e) => {

                try {

                    const data =
                        new Uint8Array(
                            e.target.result
                        );


                    const workbook =
                        XLSX.read(
                            data,
                            {
                                type: "array"
                            }
                        );


                    const sheetName =
                        workbook
                            .SheetNames[0];


                    const worksheet =
                        workbook
                            .Sheets[sheetName];


                    const importedData =
                        XLSX.utils.sheet_to_json(
                            worksheet,
                            {
                                defval: ""
                            }
                        );


                    if (
                        !importedData.length
                    ) {

                        alert(
                            "Excel file is empty."
                        );

                        return;

                    }


                    const formattedData =
                        importedData.map(
                            (
                                item,
                                index
                            ) => ({

                                id:
                                    item.ID ||
                                    Date.now() +
                                    index,

                                name:
                                    item.Name ||
                                    "",

                                age:
                                    item.Age ||
                                    "",

                                phone:
                                    item.Phone ||
                                    "",

                                secondaryPhone:
                                    item[
                                        "Secondary Phone"
                                    ] || "",

                                email:
                                    item.Email ||
                                    "",

                                secondaryEmail:
                                    item[
                                        "Secondary Email"
                                    ] || "",

                                experience:
                                    item.Experience ||
                                    "",

                                city:
                                    item.City ||
                                    "",

                                state:
                                    item.State ||
                                    "",

                                pincode:
                                    item.Pincode ||
                                    "",

                                skills:
                                    item.Skills ||
                                    "",

                                rate:
                                    item.Rate ||
                                    "",

                                source:
                                    item.Source ||
                                    "",

                                status:
                                    item.Status ||
                                    "PENDING",

                                remark:
                                    item.Remark ||
                                    "",

                                resumeName:
                                    item.Resume ||
                                    ""

                            })
                        );


                    setTrainers(prev => [

                        ...prev,

                        ...formattedData

                    ]);


                    alert(
                        `${formattedData.length} trainer(s) imported successfully.`
                    );


                } catch (error) {

                    console.error(
                        error
                    );

                    alert(
                        "Invalid Excel file."
                    );

                }

            };


            reader.readAsArrayBuffer(
                file
            );


            event.target.value = "";

        };


    // ==========================================
    // FORMAT RATE
    // ==========================================

    const formatRate =
        (rateValue) => {

            if (
                rateValue === null ||
                rateValue === undefined ||
                rateValue === ""
            ) {

                return "0";

            }


            return Number(
                rateValue
            ).toLocaleString(
                "en-IN"
            );

        };


    // ==========================================
    // RETURN
    // ==========================================

    return (

        <div className="trainer-page">


            {/* ==========================================
                HEADER
            ========================================== */}

            <div className="trainer-header">

                <div className="trainer-header-left">

                    <div className="trainer-title-icon">

                        <Users size={21} />

                    </div>


                    <div>

                        <h1>
                            Trainer Management
                        </h1>

                        <p>
                            Manage and monitor all registered trainers
                        </p>

                    </div>

                </div>


                <div className="trainer-header-actions">


                    {/* IMPORT */}

                    <button
                        className="trainer-import-btn"
                        onClick={
                            handleImportClick
                        }
                    >

                        <Upload size={15} />

                        Import Excel

                    </button>


                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{
                            display: "none"
                        }}
                        onChange={
                            handleImportExcel
                        }
                    />


                    {/* EXPORT */}

                    <button
                        className="trainer-export-btn"
                        onClick={
                            handleExportExcel
                        }
                    >

                        <FileSpreadsheet
                            size={15}
                        />

                        Export Excel

                    </button>


                    {/* ADD */}

                    <button
                        className="trainer-add-btn"
                        onClick={() =>
                            navigate(
                                "/trainer/register"
                            )
                        }
                    >

                        <Plus size={16} />

                        Add Trainer

                    </button>


                </div>

            </div>


            {/* ==========================================
                FILTER
            ========================================== */}

            <div className="trainer-filter-card">


                <div className="trainer-filter-title">

                    <div className="trainer-filter-icon">

                        <Filter size={17} />

                    </div>


                    <div>

                        <h3>
                            Trainer Filters
                        </h3>

                        <span>
                            Search and filter trainers
                        </span>

                    </div>

                </div>


                <div className="trainer-filter-grid">


                    {/* SEARCH */}

                    <div className="trainer-filter-input">

                        <Search size={16} />

                        <input
                            type="text"
                            placeholder="Search Name / Email / Phone"
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />

                    </div>


                    {/* SKILL */}

                    <select
                        value={skill}
                        onChange={(e) =>
                            setSkill(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Skills
                        </option>


                        {skills.map(
                            item => (

                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item}
                                </option>

                            )
                        )}

                    </select>


                    {/* LOCATION */}

                    <select
                        value={location}
                        onChange={(e) =>
                            setLocation(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Location
                        </option>


                        {locations.map(
                            item => (

                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item}
                                </option>

                            )
                        )}

                    </select>


                    {/* RATE */}

                    <select
                        value={rate}
                        onChange={(e) =>
                            setRate(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Commercial
                        </option>

                        <option value="3000">
                            Below ₹3,000
                        </option>

                        <option value="5000">
                            Below ₹5,000
                        </option>

                        <option value="8000">
                            Below ₹8,000
                        </option>

                        <option value="10000">
                            Below ₹10,000
                        </option>

                    </select>


                    {/* STATUS FILTER */}

                    <select
                        value={status}
                        onChange={(e) =>
                            setStatus(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Status
                        </option>

                        <option value="PENDING">
                            Pending
                        </option>

                        <option value="APPROVED">
                            Approved
                        </option>

                        <option value="REJECTED">
                            Rejected
                        </option>
<option value="INACTIVE">
        INACTIVE
    </option>

    <option value="BLOCKED">
        BLOCKED
    </option>
                    </select>


                    {/* CLEAR */}

                    <button
                        className="trainer-clear-btn"
                        onClick={
                            clearFilter
                        }
                    >

                        <RotateCcw
                            size={15}
                        />

                     

                    </button>


                </div>

            </div>


            {/* ==========================================
                TABLE
            ========================================== */}

            <div className="trainer-table-card">


                <div className="trainer-table-header">


                    <div>

                        <h2>
                            Trainer List
                        </h2>

                        <span>

                            Showing{" "}

                            {
                                filteredTrainers.length
                            }

                            {" "}trainers

                        </span>

                    </div>


                    {/* PAGINATION */}

                    <div className="trainer-pagination">


                        <button
                            disabled={
                                currentPage === 1
                            }
                            onClick={() =>
                                changePage(
                                    currentPage - 1
                                )
                            }
                        >
                            ‹
                        </button>


                        {Array.from(
                            {
                                length:
                                    totalPages
                            },
                            (
                                _,
                                index
                            ) => (

                                <button
                                    key={index}
                                    className={
                                        currentPage ===
                                        index + 1
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        changePage(
                                            index + 1
                                        )
                                    }
                                >

                                    {
                                        index + 1
                                    }

                                </button>

                            )
                        )}


                        <button
                            disabled={
                                currentPage ===
                                    totalPages ||
                                totalPages === 0
                            }
                            onClick={() =>
                                changePage(
                                    currentPage + 1
                                )
                            }
                        >
                            ›
                        </button>


                    </div>


                </div>


                {/* TABLE WRAPPER */}

                <div className="trainer-table-wrapper">


                    <table className="trainer-table">


                        <thead>

                            <tr>

                                <th>ID</th>

                                <th>Name</th>

                                <th>Contact</th>

                                <th>Email</th>

                                <th>Experience</th>

                                <th>Location</th>

                                <th>Skills</th>

                                <th>Rate</th>

                                <th>Source</th>

                                <th>Status</th>

                                <th>Remark</th>

                                <th>Resume</th>

                                <th>Action</th>

                            </tr>

                        </thead>


                        <tbody>


                            {currentTrainers.length >
                            0 ? (

                                currentTrainers.map(
                                    trainer => (

                                        <tr
                                            key={
                                                trainer.id
                                            }
                                        >


                                            {/* ID */}

                                            <td>

                                                {
                                                    trainer.id
                                                }

                                            </td>


                                            {/* NAME */}

                                            <td>

                                                <div className="trainer-name">

                                                    <strong>
                                                        {
                                                            trainer.name
                                                        }
                                                    </strong>

                                                    <span>
                                                        Age:{" "}
                                                        {
                                                            trainer.age ||
                                                            "-"
                                                        }
                                                    </span>

                                                </div>

                                            </td>


                                            {/* CONTACT */}

                                            <td>

                                                <div className="trainer-contact">

                                                    <span>
                                                        {
                                                            trainer.phone ||
                                                            "-"
                                                        }
                                                    </span>


                                                    {
                                                        trainer.secondaryPhone && (

                                                            <small>

                                                                Alt:{" "}

                                                                {
                                                                    trainer.secondaryPhone
                                                                }

                                                            </small>

                                                        )
                                                    }

                                                </div>

                                            </td>


                                            {/* EMAIL */}

                                            <td>

                                                <div className="trainer-email">

                                                    <span>
                                                        {
                                                            trainer.email ||
                                                            "-"
                                                        }
                                                    </span>


                                                    {
                                                        trainer.secondaryEmail && (

                                                            <small>

                                                                {
                                                                    trainer.secondaryEmail
                                                                }

                                                            </small>

                                                        )
                                                    }

                                                </div>

                                            </td>


                                            {/* EXPERIENCE */}

                                            <td>

                                                {
                                                    trainer.experience ||
                                                    "-"
                                                }

                                                {" "}Years

                                            </td>


                                            {/* LOCATION */}

                                            <td>

                                                <div className="trainer-location">

                                                    <strong>
                                                        {
                                                            trainer.city ||
                                                            "-"
                                                        }
                                                    </strong>

                                                    <span>
                                                        {
                                                            trainer.state ||
                                                            ""
                                                        }
                                                    </span>

                                                    <small>
                                                        {
                                                            trainer.pincode ||
                                                            ""
                                                        }
                                                    </small>

                                                </div>

                                            </td>


                                            {/* SKILLS */}

                                            <td>

                                                <div className="trainer-skill-box">

                                                    {
                                                        trainer.skills
                                                            ?.split(",")
                                                            .map(
                                                                (
                                                                    item,
                                                                    index
                                                                ) => (

                                                                    <span
                                                                        className="trainer-skill-tag"
                                                                        key={
                                                                            index
                                                                        }
                                                                    >

                                                                        {
                                                                            item.trim()
                                                                        }

                                                                    </span>

                                                                )
                                                            )
                                                    }

                                                </div>

                                            </td>


                                            {/* RATE */}

                                            <td>

                                                <div className="trainer-rate">

                                                    ₹{" "}

                                                    {
                                                        formatRate(
                                                            trainer.rate
                                                        )
                                                    }

                                                    <small>
                                                        / Day
                                                    </small>

                                                </div>

                                            </td>


                                            {/* SOURCE */}

                                            <td>

                                                {
                                                    trainer.source ||
                                                    "-"
                                                }

                                            </td>


                                            {/* ==========================================
                                                STATUS
                                            ========================================== */}

                                            <td>

                                                <select
                                                    className={`trainer-status-select ${getStatusClass(
                                                        trainer.status
                                                    )}`}
                                                    value={
                                                        trainer.status ||
                                                        "PENDING"
                                                    }
                                                    disabled={
                                                        updatingStatusId ===
                                                        trainer.id
                                                    }
                                                    onChange={(
                                                        e
                                                    ) =>
                                                        handleStatusChange(
                                                            trainer.id,
                                                            e.target.value
                                                        )
                                                    }
                                                >

                                                    <option value="PENDING">
                                                        PENDING
                                                    </option>

                                                    <option value="APPROVED">
                                                        APPROVED
                                                    </option>

                                                    <option value="REJECTED">
                                                        REJECTED
                                                    </option>
<option value="INACTIVE">
        INACTIVE
    </option>

    <option value="BLOCKED">
        BLOCKED
    </option>
                                                </select>


                                                {
                                                    updatingStatusId ===
                                                        trainer.id && (

                                                        <small
                                                            style={{
                                                                display:
                                                                    "block",
                                                                marginTop:
                                                                    "4px",
                                                                fontSize:
                                                                    "10px"
                                                            }}
                                                        >
                                                            Updating...
                                                        </small>

                                                    )
                                                }

                                            </td>


                                            {/* REMARK */}

                                            <td>

                                                <div className="trainer-remark">

                                                    {
                                                        trainer.remark ||
                                                        "-"
                                                    }

                                                </div>

                                            </td>


                                            {/* RESUME */}

                                            <td>

                                                {
                                                    trainer.resumeName ? (

                                                        <a
                                                            className="trainer-icon-btn download"
                                                            title="Download Resume"
                                                            href={`/api/trainers/resume/${trainer.id}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >

                                                            <Download
                                                                size={
                                                                    16
                                                                }
                                                            />

                                                        </a>

                                                    ) : (

                                                        "-"

                                                    )
                                                }

                                            </td>


                                            {/* ACTION */}

                                            <td>

                                                <div className="trainer-action-icons">


                                                    {/* EDIT */}

                                                    <button
                                                        type="button"
                                                        className="trainer-icon-btn edit"
                                                        title="Edit Trainer"
                                                        onClick={() =>
                                                            navigate(
                                                                `/trainer/edit/${trainer.id}`
                                                            )
                                                        }
                                                    >

                                                        <Edit
                                                            size={
                                                                16
                                                            }
                                                        />

                                                    </button>


                                                    {/* DELETE */}

                                                    <button
                                                        type="button"
                                                        className="trainer-icon-btn delete"
                                                        title="Delete Trainer"
                                                        onClick={() =>
                                                            handleDelete(
                                                                trainer.id
                                                            )
                                                        }
                                                    >

                                                        <Trash2
                                                            size={
                                                                16
                                                            }
                                                        />

                                                    </button>


                                                </div>

                                            </td>


                                        </tr>

                                    )
                                )

                            ) : (

                                <tr>

                                    <td
                                        colSpan="13"
                                        className="trainer-empty"
                                    >

                                        No Trainer Found

                                    </td>

                                </tr>

                            )}


                        </tbody>

                    </table>

                </div>

            </div>

        </div>

    );

}


export default TrainerList;